/**
 * @file EDALabLayout.js
 * @module src/js/layouts/EDALabLayout
 * @description
 * **Defines the shared layout for all public pages of the EDALab website.**
 * - Wraps all content in a consistent layout for public routes.
 * - Verifies user session token on mount.
 * - Scrolls to top or target element based on navigation state.
 * - Dynamic title generation based on current pathname.
 * - Handles touch and mouse interactions for responsive menu behavior.
 *
 * @version
 * <p>
 *  2025-07-22: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Added `ImageModal` component for global image modal support. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useInitTokenTest hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeProvider';
import useInitTokenTest from '../hooks/useInitTokenTest';
import Header from '../components/Header';
import Banner from '../components/Banner';
import Footer from '../components/Footer';
import Menu from '../components/Menu';
import DarkOverlay from '../components/DarkOverlay';
import FloatingActions from '../components/FloatingActions';
import ImageModal from '../modals/ImageModal';
import MessageModal from '../modals/MessageModal';
import getDevice from '../functions/getDevice';

export default function EDALabLayout() {
    const navigate = useNavigate();
    const { key, pathname, state } = useLocation();
    const { setTheme } = useTheme();
    const initTokenTest = useInitTokenTest();
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const [isHeaderAndFloatingActionsVisible, setIsHeaderAndFloatingActionsVisible] = useState(false);
    const [headerBgOpacity, setHeaderBgOpacity] = useState(0);
    const menuRef = useRef(null);
    const { hasTouchScreen } = getDevice();

    useEffect(() => {
        initTokenTest({ showModal: false, navigateToLogin: false });
    }, [initTokenTest]);

    useEffect(() => {
        const scrollToPosition = () => {
            if (pathname === "/") {
                if (state?.scrollTo) {
                    const element = document.getElementById(state?.scrollTo);
                    if (element) {
                        [250, 500, 750].forEach((delay) => {
                            setTimeout(() => {
                                element.scrollIntoView({ behavior: "smooth" });
                            }, delay);
                        });
                    }
                } else {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                }
            } else {
                const bannerHeight = document.getElementById("banner")?.offsetHeight || 0;
                [200, 400].forEach((delay) => { // scroll multiple times to ensure scrolling after page content loads
                    setTimeout(() => {
                        window.scrollTo({ top: bannerHeight, behavior: "smooth" });
                    }, delay);
                });
            }
        };
        // Call scrolling function on mount and when key changes
        // Weird thing: When navigating from "/" to "/login", the page will not fully scroll for bannerHeight
        // So we need to use setTimeout to delay the scrolling
        setTimeout(() => scrollToPosition(), pathname === "/login" ? 100 : 0);
    }, [pathname, key, state?.scrollTo]);
    
    useEffect(() => {
        if (isMenuVisible) {
            document.body.classList.add("is-menu-visible");
        } else {
            document.body.classList.remove("is-menu-visible");
        }

        // Cleanup function to remove class when component unmounts
        return () => document.body.classList.remove("is-menu-visible");
    }, [isMenuVisible]);

    useEffect(() => {
        const handleClickOutsideMenu = (e) => {
            if (!menuRef.current?.contains(e.target)) {
                setIsMenuVisible(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutsideMenu);
        return () => document.removeEventListener("mousedown", handleClickOutsideMenu);
    }, []);

    const handleClickContactUs = useCallback(() => {
        if (pathname === '/') {
            window.scrollTo({ top: document.getElementById('contact-us').offsetTop, behavior: 'smooth' })
        } else {
            navigate('/', { state: { scrollTo: 'contact-us' } })
        }
    }, [pathname, navigate]);


    useEffect(() => {
        const determineHeaderStyle = () => {
            const bannerHeight = document.getElementById('banner')?.offsetHeight || 0;
            const headerHeight = document.getElementById('header')?.offsetHeight || 0;
            const r = 0.05; // ratio to trigger visibility
            const d1 = window.scrollY - (bannerHeight - headerHeight) * r;
            const d2 = (bannerHeight - headerHeight) * (1 - r);

            setIsHeaderAndFloatingActionsVisible(d1 >= 0);
            const clippedRatio = Math.max(0, Math.min(1, d1 / d2));
            // y = x(2 - x) grows fastest than y = x in [0, 1]
            setHeaderBgOpacity(clippedRatio * (2 - clippedRatio));
        };
        window.addEventListener('scroll', determineHeaderStyle);
        return () => window.removeEventListener('scroll', determineHeaderStyle);
    }, []);

    // Automatically switch theme when the next 06:00 / 18:00 boundary is reached
    useEffect(() => {
        const scheduleNextSwitch = () => {
            const now = new Date();
            const hour = now.getHours();
            const nextSwitch = new Date(now);
            if (hour < 6) {
                // Next switch is today at 06:00
                nextSwitch.setHours(6, 0, 0, 0);
            } else if (hour >= 6 && hour < 18) {
                // Next switch is today at 18:00
                nextSwitch.setHours(18, 0, 0, 0);
            } else {
                // After 18:00, next switch is tomorrow at 06:00
                nextSwitch.setDate(nextSwitch.getDate() + 1);
                nextSwitch.setHours(6, 0, 0, 0);
            }
            const delay = nextSwitch.getTime() - now.getTime();
            const timerId = window.setTimeout(() => {
                // Set theme according to the new time
                const hour = new Date().getHours();
                setTheme(hour < 6 || hour >= 18 ? 'dark' : 'light');
                // Schedule the next switch after this one fires
                scheduleNextSwitch();
            }, delay > 0 ? delay + 1000 : 12 * 60 * 60 * 1000); // Add 1 second buffer or fallback to 12 hours
            return timerId;
        };
        const timerId = scheduleNextSwitch();
        return () => {
            window.clearTimeout(timerId);
        };
    }, [setTheme]);

    return (
        <>
            <title>
                {pathname === "/" ? "NTU EDA Lab" : 
                    pathname.split("/").filter(Boolean)
                        .map(word => 
                            word.split("-").map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(" ")
                        )
                        .join(" - ") // pathname: /abc/def-ghi/jkl => title: Abc - Def Ghi - Jkl
                }
            </title>
            <meta
                name="description"
                content="Electronic Design Automation (EDA) Laboratory at National Taiwan University (NTU)"
            />
            <DarkOverlay />
            <div id="page-wrapper">
                <Header
                    handleMouseEnterMenu={hasTouchScreen ? null : () => setIsMenuVisible(true)}
                    handleClickMenu={hasTouchScreen ? () => setIsMenuVisible(true) : null}
                    isHeaderVisible={isHeaderAndFloatingActionsVisible}
                    headerBgOpacity={headerBgOpacity}
                />
                <Banner handleClickContactUs={handleClickContactUs} />
                <Outlet /> {/* This renders child routes like "/..." */}
                <Footer />
            </div>
            <Menu
                handleMouseLeaveMenu={hasTouchScreen ? null : () => setIsMenuVisible(false)}
                menuRef={menuRef}
                setIsMenuVisible={setIsMenuVisible}
            />
            <FloatingActions
                handleClickContactUs={handleClickContactUs}
                isFloatingActionsVisible={isHeaderAndFloatingActionsVisible}
            />
            <ImageModal />
            <MessageModal />
        </>
    );
}
